import { animate, state, style, transition, trigger } from '@angular/animations';
import { LayoutModule } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  computed,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MAT_SELECT_CONFIG, MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { RxState } from '@rx-angular/state';
import { DigitOnlyModule } from '@uiowa/digit-only';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { CountryCode, ParsedNumber } from 'libphonenumber-js';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { Observable, Subject, combineLatest } from 'rxjs';
import { distinctUntilChanged, map, shareReplay, startWith, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { FieldValidation } from '../../field-validation';
import { InlineEditService } from '../../inline-edit.service';
import { CrmFieldWarningDirective } from '../model-driven-form-error/field-warning.directive';
import { FieldWarningService } from '../model-driven-form-error/field-warning.service';
import { CrmModelDrivenFormErrorComponent } from '../model-driven-form-error/model-driven-form-error.component';
import { CrmModelDrivenPopoverComponent } from '../model-driven-popover/model-driven-popover.component';
import { stopChromeAutofill } from '../stop-chrome-autofill';
import { UpdaterService } from '../updater.service';
import { CountryCodeService } from './country-code.service';
import { CountryOption, countryOptionsFactoryProvider } from './country-options';
import {
  formatAsRFC3966Phone,
  formatAsReadonlyPhone,
  parsePhoneNumber,
  validateBasedOnPhoneFormGroup,
  validatePhoneNumber,
} from './utils';
import { ObjectType } from '../../../../tokens-and-interfaces';

@Component({
  templateUrl: './phone.html',
  imports: [
    CommonModule,
    MatInputModule,
    ReactiveFormsModule,
    TranslateModule,
    GalaxyFormFieldModule,
    LayoutModule,
    DigitOnlyModule,
    MatSelectModule,
    NgxMatSelectSearchModule,
    CrmModelDrivenFormErrorComponent,
    CrmFieldWarningDirective,
    CrmModelDrivenPopoverComponent,
  ],
  providers: [
    RxState,
    countryOptionsFactoryProvider,
    CountryCodeService,
    {
      provide: MAT_SELECT_CONFIG,
      useValue: { overlayPanelClass: 'crm-phone-country-selector-overlay-pane' },
    },
    FieldWarningService,
  ],
  styleUrls: ['../renderers.scss', './phone.scss'],
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('autoFill', [
      state(
        'highlight',
        style({
          backgroundColor: '#e3f2fd',
        }),
      ),
      state(
        'default',
        style({
          backgroundColor: '#ffffff',
        }),
      ),
      transition('default => highlight', [animate('1s')]),
      transition('highlight => default', [animate('1s')]),
    ]),
  ],
})
export class PhoneRendererComponent implements OnInit, OnDestroy {
  searchCountryControl = new FormControl('');
  private countryControl = new FormControl('US');
  private phoneNumberControl = new FormControl('');
  private extensionControl = new FormControl('');
  phone: FormGroup = new FormGroup({
    country: this.countryControl,
    phoneNumber: this.phoneNumberControl,
    extension: this.extensionControl,
  });
  initialValue = '';
  objectType!: ObjectType;
  control!: UntypedFormControl;
  label!: string;
  fieldId!: string;
  validation!: FieldValidation;
  readOnly!: boolean;
  isInlineEdit$ = this.inlineEditService.isInlineEdit$;

  private parsedPhoneNumber = signal<ParsedNumber>({
    country: 'US',
    phone: '',
  } as ParsedNumber);
  readonlyPhoneNumber = computed<string>(() => formatAsReadonlyPhone(this.parsedPhoneNumber()));
  RFC3966PhoneNumber = computed<string>(() => formatAsRFC3966Phone(this.parsedPhoneNumber()));
  private RFC3966PhoneNumber$ = toObservable(this.RFC3966PhoneNumber);
  private destroyed$$ = new Subject<void>();

  disableAutoComplete: string = stopChromeAutofill();
  countryOptions$: Observable<CountryOption[]> = this.countryCodeService.countryOptions$.pipe(
    distinctUntilChanged(),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );
  filteredCountryOptions$: Observable<CountryOption[]> = this.searchCountryControl.valueChanges.pipe(
    startWith(''),
    withLatestFrom(this.countryOptions$),
    map(([filterValue, countryOptions]) => {
      return countryOptions.filter((c) => {
        if (c.callingCode.includes(filterValue)) {
          return true;
        }
        if (c.countryCode.toLowerCase().includes(filterValue.toLowerCase())) {
          return true;
        }
        if (c.countryName.toLowerCase().includes(filterValue.toLowerCase())) {
          return true;
        }
        return false;
      });
    }),
  );
  selectedCountry = computed(() => {
    return this.countryCodeService.getCountryOption(this.parsedPhoneNumber().country as CountryCode);
  });
  selectedCountryCallingCode = computed(() => this.selectedCountry()?.callingCode || '');

  autoFillState = 'default';

  constructor(
    private readonly countryCodeService: CountryCodeService,
    private changeDetRef: ChangeDetectorRef,
    private updaterService: UpdaterService,
    private readonly inlineEditService: InlineEditService,
  ) {
    this.phone.addValidators(validatePhoneNumber);
    // set precedence for these two country codes at all times on init
    this.countryCodeService.countryCodeUsed('CA');
    this.countryCodeService.countryCodeUsed('US');
  }

  ngOnInit(): void {
    this.initialValue = this.control.value;
    this.setupPhoneFormGroup();
    this.setupControlValuesForForm();

    this.updaterService.updatedField
      .pipe(
        takeUntil(this.destroyed$$),
        tap((data) => {
          if (data.stringValue && data.stringValue != '' && data.fieldId && data.fieldId === this.fieldId) {
            this.autoFillState = 'highlight';
            setTimeout(() => {
              this.autoFillState = 'default';
              this.changeDetRef.detectChanges();
            }, 1200);
            this.setPhoneValue(data.stringValue);
            this.changeDetRef.detectChanges();
          }
        }),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroyed$$.next();
    this.destroyed$$.complete();
  }

  private setPhoneValue(phoneValue: string): [string, string, string] {
    let startingCountry = 'US';
    let startingPhoneNumber = phoneValue;
    let startingExtension = '';
    if (phoneValue) {
      const parsedPhoneNumber = parsePhoneNumber(phoneValue);
      startingCountry = parsedPhoneNumber.country || 'US';
      startingPhoneNumber = parsedPhoneNumber.phone || phoneValue;
      startingExtension = parsedPhoneNumber.ext || '';
      this.parsedPhoneNumber.set(parsedPhoneNumber);
    }
    this.countryControl.setValue(startingCountry);
    this.countryCodeService.countryCodeUsed(startingCountry as CountryCode);
    this.phoneNumberControl.setValue(startingPhoneNumber);
    this.extensionControl.setValue(startingExtension);

    return [startingCountry, startingPhoneNumber, startingExtension];
  }

  private setupPhoneFormGroup(): void {
    const [startingCountry, startingPhoneNumber, startingExtension] = this.setPhoneValue(this.control.value);

    const countryValue$ = this.countryControl.valueChanges.pipe(startWith(startingCountry));
    const phoneNumberValue$ = this.phoneNumberControl.valueChanges.pipe(startWith(startingPhoneNumber));
    const extensionValue$ = this.extensionControl.valueChanges.pipe(startWith(startingExtension));
    combineLatest([countryValue$, phoneNumberValue$, extensionValue$])
      .pipe(
        takeUntil(this.destroyed$$),
        map(
          ([country, phone, ext]) =>
            ({
              country,
              phone,
              ext,
            }) as ParsedNumber,
        ),
        tap((parsedNumber) => this.parsedPhoneNumber.set(parsedNumber)),
      )
      .subscribe();
  }

  private setupControlValuesForForm(): void {
    this.control.addValidators(validateBasedOnPhoneFormGroup(this.phone));
    this.RFC3966PhoneNumber$.pipe(
      takeUntil(this.destroyed$$),
      tap((phoneNumber) => {
        this.control.setValue(phoneNumber);
        if (phoneNumber != this.initialValue) {
          this.control.markAsDirty();
        } else {
          this.control.markAsPristine();
        }
      }),
    ).subscribe();
  }

  saveField(): void {
    this.inlineEditService.saveInlineEdit(this.objectType, {
      fieldId: this.fieldId,
      stringValue: this.control.value,
    });
  }
}
